import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import Avatar from '@atlaskit/avatar';
import { LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { CheckboxField, ErrorMessage, Field } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { H2 } from '@atlassian/amkt-common';
import { postReview } from '@atlassian/mpac-api-client';
import { HostingDisplayName, HostingTypeToQueryParam } from '@atlassian/mpac-types';
import { ExternalLink, RatingsSelector, useFeatureFlags } from '@atlassian/mpac-ui';
import { log, UrlUtils } from '@atlassian/mpac-utils';
import React, { useEffect } from 'react';
import { useAsync } from 'react-async';
import { Link } from 'react-router-dom';
import { AppListingTab, TEST_ID } from "../../../../../constants";
import { AvatarContainer, FormFooterContainer, LeftFooter, PostReviewErrorContainer, RatingsField, ReviewFormContainer, RightFooter, SubmitButtonContainer, TextAreaContainer, WriteReviewContainer } from "./ReviewForm.styles";
import { validateOnSubmit, validateRatings, validateUserComplianceField } from "./ReviewForm.utils";
// eslint-disable-next-line max-lines-per-function
const ReviewForm = ({
  postReviewUrl,
  hosting,
  onReviewPost,
  userAvatarUrl,
  selfUserReview,
  isReviewApplicableForComplianceLogic
}) => {
  const {
    isPending: isPostReviewInProgress,
    isRejected: isPostReviewRejected,
    run: runPostReview,
    data: postedReview,
    error: postReviewError
  } = useAsync({
    deferFn: postReview
  });
  const {
    enableReviewComplianceUi
  } = useFeatureFlags();
  const postedReviewId = postedReview === null || postedReview === void 0 ? void 0 : postedReview.id;
  const userConsentCheckBoxLabel = enableReviewComplianceUi ? 'I am currently using this app or have used it some time in last six months.' : 'I am a current user of this app or have used it in recent past.';
  useEffect(() => {
    if (isPostReviewRejected) {
      log.error('ReviewsTab', 'failed to post a review', {
        postReviewError
      });
    }
  }, [isPostReviewRejected]);
  useEffect(() => {
    if (!postedReviewId) return;
    onReviewPost(postedReview);
  }, [postedReviewId]);
  const onReviewSubmit = data => {
    const errors = validateOnSubmit(data);
    const reviewStatus = (selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.status) === 'approved' ? 'approved' : 'pending';
    if (Object.keys(errors).length === 0) {
      var _data$review;
      const postReviewData = _objectSpread({
        userHasComplianceConsent: data.userHasComplianceConsent,
        review: (_data$review = data.review) !== null && _data$review !== void 0 ? _data$review : '',
        stars: data.ratings,
        hosting: HostingTypeToQueryParam[hosting]
      }, isReviewApplicableForComplianceLogic && {
        status: reviewStatus
      });
      runPostReview(postReviewUrl, postReviewData);
    }
    return errors;
  };
  const supportTabLink = UrlUtils.updateQueryParam(window.location.href, 'tab', AppListingTab.Support);
  return /*#__PURE__*/React.createElement(ReviewFormContainer, null, /*#__PURE__*/React.createElement(AvatarContainer, null, /*#__PURE__*/React.createElement(Avatar, {
    appearance: "circle",
    src: userAvatarUrl,
    size: "large",
    testId: "review-author-avatar"
  })), /*#__PURE__*/React.createElement(WriteReviewContainer, null, /*#__PURE__*/React.createElement(H2, null, "Write a review for ", HostingDisplayName[hosting]), isPostReviewRejected ? /*#__PURE__*/React.createElement(PostReviewErrorContainer, null, "We couldn't save your review due to an error. If this problem persists, please contact", ' ', /*#__PURE__*/React.createElement(ExternalLink, {
    href: "http://go.atlassian.com/marketplace-support"
  }, "Marketplace support"), ".") : null, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onReviewSubmit,
    isDisabled: isPostReviewInProgress
  }, ({
    formProps
  }) => /*#__PURE__*/React.createElement("form", formProps, /*#__PURE__*/React.createElement(Field, {
    name: "review",
    defaultValue: selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.review
  }, ({
    fieldProps
  }) => /*#__PURE__*/React.createElement(TextAreaContainer, null, /*#__PURE__*/React.createElement(TextArea, _extends({
    appearance: "standard",
    resize: "auto",
    maxHeight: "30vh",
    minimumRows: 6,
    name: "review",
    placeholder: "Write your review here",
    testId: TEST_ID.REVIEWS_TAB_REVIEW_FORM_TEXTAREA
  }, fieldProps)))), /*#__PURE__*/React.createElement(CheckboxField, {
    name: "userHasComplianceConsent",
    validate: validateUserComplianceField,
    defaultIsChecked: !!selfUserReview
  }, ({
    fieldProps,
    error
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Checkbox, _extends({}, fieldProps, {
    testId: TEST_ID.REVIEWS_TAB_REVIEW_FORM_CHECKBOX,
    label: userConsentCheckBoxLabel
  })), error && /*#__PURE__*/React.createElement(ErrorMessage, null, error))), /*#__PURE__*/React.createElement(FormFooterContainer, null, /*#__PURE__*/React.createElement(LeftFooter, null, "Need help?\xA0 ", /*#__PURE__*/React.createElement(Link, {
    to: supportTabLink
  }, "Get Support")), /*#__PURE__*/React.createElement(RightFooter, null, /*#__PURE__*/React.createElement(RatingsField, null, /*#__PURE__*/React.createElement(Field, {
    name: "ratings",
    defaultValue: selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.stars,
    validate: validateRatings
  }, ({
    fieldProps,
    error
  }) => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RatingsSelector, {
      onRatingsChange: fieldProps.onChange,
      defaultRating: selfUserReview === null || selfUserReview === void 0 ? void 0 : selfUserReview.stars
    }), error && /*#__PURE__*/React.createElement(ErrorMessage, null, error));
  })), /*#__PURE__*/React.createElement(SubmitButtonContainer, null, /*#__PURE__*/React.createElement(LoadingButton, {
    type: "submit",
    appearance: "warning",
    isLoading: isPostReviewInProgress,
    isDisabled: isPostReviewInProgress,
    testId: TEST_ID.REVIEWS_TAB_PUBLISH_REVIEW_BUTTON
  }, "Publish"))))))));
};
export default ReviewForm;